<template>
  <div
    class="ai-suggest-bar"
    v-if="visible"
    :class="{ 'is-collapsed': collapsed }"
  >
    <div class="ai-suggest-bar__head">
      <span
        class="ai-suggest-bar__label"
        @click="collapsed && toggleCollapse()"
      >
        智能话术
        <span class="ai-suggest-bar__count" v-if="items.length">
          <i>{{ items.length }}</i
          ><em>条</em>
        </span>
      </span>
      <div class="ai-suggest-bar__actions">
        <button
          type="button"
          class="ai-suggest-bar__btn"
          @click="toggleCollapse"
        >
          {{ collapsed ? "展开" : "收起" }}
        </button>
        <button type="button" class="ai-suggest-bar__btn" @click="dismiss">
          ×
        </button>
      </div>
    </div>

    <div class="ai-suggest-bar__list" v-show="!collapsed">
      <button
        v-for="(item, index) in items"
        :key="item.id || index"
        type="button"
        class="ai-suggest-bar__chip"
        :class="modelThemeClass(item.modelName)"
        @click="useItem(item)"
      >
        <span class="ai-suggest-bar__meta">
          <img
            v-if="modelLogo(item.modelName)"
            class="ai-suggest-bar__logo"
            :src="modelLogo(item.modelName)"
            :alt="item.modelName"
            :title="item.modelName"
            @error="onLogoError($event, item)"
          />
          <i
            v-if="item.modelName && (!modelLogo(item.modelName) || item._logoBroken)"
            class="ai-suggest-bar__name"
            >{{ item.modelName }}</i
          >
        </span>
        <span class="ai-suggest-bar__text">{{ displayContent(item) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  fillAgentPlaceholders,
  getAgentWechat,
} from "../../utils/aiAssistant.js";

const LOGO_BASE = (process.env.BASE_URL || "/") + "logos/ai/";
const LOGO_QWEN = LOGO_BASE + "qwen.png";
const LOGO_DOUBAO = LOGO_BASE + "doubao.png";
const LOGO_DEEPSEEK = LOGO_BASE + "deepseek.png";
const LOGO_ZHIPU = LOGO_BASE + "zhipu.png";

const MODEL_THEME_RULES = [
  {
    key: "qwen",
    test: /(\u5343\u95ee|\u901a\u4e49|qwen|tongyi)/i,
    logo: LOGO_QWEN,
  },
  {
    key: "doubao",
    test: /(\u8c46\u5305|doubao|bytedance)/i,
    logo: LOGO_DOUBAO,
  },
  {
    key: "deepseek",
    test: /(deepseek|\u6df1\u5ea6\u6c42\u7d22)/i,
    logo: LOGO_DEEPSEEK,
  },
  {
    key: "zhipu",
    test: /(\u667a\u8c31|zhipu|glm|chatglm)/i,
    logo: LOGO_ZHIPU,
  },
];

export default {
  name: "AiSuggestBar",
  computed: {
    suggest() {
      return this.$store.state.aiAssistantSuggest || {};
    },
    collapsed() {
      return !!this.suggest.collapsed;
    },
    currentClient() {
      if (this.$store.state.colleageAngle) {
        return this.$store.state.college.nowSelectClient;
      }
      return this.$store.state.selectedClient;
    },
    items() {
      const list = this.suggest.items || [];
      const cid = this.suggest.cid;
      const sid = this.suggest.sid;
      const client = this.currentClient;
      if (!client) return [];
      if (cid && client.cid && cid !== client.cid) return [];
      if (sid && client.sid && sid !== client.sid) return [];
      return list;
    },
    visible() {
      return this.items.length > 0;
    },
  },
  methods: {
    matchModel(name) {
      if (!name) return null;
      return MODEL_THEME_RULES.find((rule) => rule.test.test(String(name))) || null;
    },
    modelLogo(name) {
      const hit = this.matchModel(name);
      return hit ? hit.logo : "";
    },
    modelThemeClass(name) {
      const hit = this.matchModel(name);
      return hit ? "is-" + hit.key : "is-default";
    },
    onLogoError(e, item) {
      if (e && e.target) e.target.style.display = "none";
      if (item) this.$set(item, "_logoBroken", true);
    },
    displayContent(item) {
      return fillAgentPlaceholders((item && item.content) || "");
    },
    useItem(item) {
      if (!item || !item.content) return;
      const raw = String(item.content);
      if (/\$\{\s*agent_wechat\s*\}/i.test(raw) && !getAgentWechat()) {
        this.$message.warning("请先在「个人设置 - 基础信息」中填写微信号");
      }
      this.$emit("use", fillAgentPlaceholders(raw));
    },
    toggleCollapse() {
      this.$store.commit("collapseAiAssistantSuggest", !this.collapsed);
    },
    dismiss() {
      this.$store.commit("clearAiAssistantSuggest");
    },
  },
};
</script>

<style scoped lang="scss">
.ai-suggest-bar {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 8px 5px;
  padding: 6px 8px;
  background: #f7f9fc;
  border: 1px solid #e3ebf5;
  border-radius: 6px;
}

.ai-suggest-bar__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ai-suggest-bar__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #2c4a6e;
  white-space: nowrap;
  cursor: default;
  line-height: 1;
}

.ai-suggest-bar__count {
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
  height: 20px;
  padding: 0 8px;
  border-radius: 4px;
  background: #eef4fb;
  border: 1px solid #d5e3f4;
  color: #3d6ea8;
  box-sizing: border-box;

  i {
    font-style: normal;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    font-variant-numeric: tabular-nums;
  }

  em {
    font-style: normal;
    font-size: 11px;
    font-weight: 500;
    line-height: 18px;
    opacity: 0.8;
  }
}

.is-collapsed .ai-suggest-bar__label {
  cursor: pointer;
}

.ai-suggest-bar__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow-x: hidden;
  overflow-y: auto;
}

.ai-suggest-bar__list::-webkit-scrollbar {
  width: 4px;
}

.ai-suggest-bar__list::-webkit-scrollbar-thumb {
  background: #c5d4e8;
  border-radius: 2px;
}

.ai-suggest-bar__chip {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-left-width: 3px;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease, filter 0.15s ease;
}

.ai-suggest-bar__chip:hover {
  filter: brightness(0.98);
  box-shadow: 0 1px 6px rgba(31, 78, 140, 0.1);
}

/* 千问：淡紫 */
.ai-suggest-bar__chip.is-qwen {
  background: linear-gradient(90deg, #f3efff 0%, #faf8ff 100%);
  border-color: #ddd0ff;
  border-left-color: #7c6af2;
  color: #3d3566;
}

/* 豆包：淡蓝 */
.ai-suggest-bar__chip.is-doubao {
  background: linear-gradient(90deg, #eaf3ff 0%, #f5f9ff 100%);
  border-color: #c9ddff;
  border-left-color: #3b82f6;
  color: #1e3a5f;
}

/* DeepSeek：淡青蓝 */
.ai-suggest-bar__chip.is-deepseek {
  background: linear-gradient(90deg, #e8f6fb 0%, #f4fbfd 100%);
  border-color: #bfe6f2;
  border-left-color: #0ea5c6;
  color: #164e63;
}

/* 智谱：淡橙 */
.ai-suggest-bar__chip.is-zhipu {
  background: linear-gradient(90deg, #fff1e8 0%, #fff8f3 100%);
  border-color: #ffd7bf;
  border-left-color: #f0733a;
  color: #7c2d12;
}

/* 未知模型 */
.ai-suggest-bar__chip.is-default {
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 100%);
  border-color: #d5dee8;
  border-left-color: #64748b;
  color: #334155;
}

.ai-suggest-bar__meta {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 1px;
  line-height: 1.45;
}

.ai-suggest-bar__logo {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: contain;
  flex: none;
  display: block;
  background: rgba(255, 255, 255, 0.7);
}

.ai-suggest-bar__name {
  font-style: normal;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  opacity: 0.85;
}

.ai-suggest-bar__text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.ai-suggest-bar__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-suggest-bar__btn {
  border: none;
  background: transparent;
  color: #7a93b5;
  cursor: pointer;
  padding: 0 4px;
  font-size: 13px;
  line-height: 20px;
}

.ai-suggest-bar__btn:hover {
  color: #1f4e8c;
}
</style>
